import { Tooltip } from '@mantine/core';
import { Icon } from '@uag/react-core';
import {
    Actions,
    DockLocation,
    Layout,
    Model,
    TabNode,
    TabSetNode,
    type BorderNode,
    type IJsonTabNode,
    type ITabRenderValues,
    type ITabSetRenderValues
} from 'flexlayout-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { ContentContainer } from 'app/modules/wallforming/components/content-container/ContentContainer';
import { InputContextStatus } from 'app/modules/wallforming/components/content-container/scene-status-bar/InputContextStatus';
import { mainModelJson } from 'app/modules/wallforming/components/data/mainModelJson';
import { wallFormingModelJson } from 'app/modules/wallforming/components/data/wallFormingModelJson';
import { useCopilotLayout } from 'app/modules/wallforming/components/setting-container/hooks/useCopilotLayout';
import { SettingContainer } from 'app/modules/wallforming/components/setting-container/SettingContainer';
import { SettingNavigatorContainer } from 'app/modules/wallforming/components/setting-container/SettingNavigatorContainer';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

import 'flexlayout-react/style/light.css';
import 'app/modules/wallforming/custom-styles/customFlexlayoutStyle.css';
import 'app/modules/wallforming/custom-styles/customAnimationStyle.css';

export const FlexLayoutContainer = () => {
    const { t } = useTranslation();
    const settingModelRef = useRef<Model | null>(null);
    const settingTabJsonRef = useRef<IJsonTabNode | null>(null);
    const settingTabsetWeightRef = useRef<number>(30);
    const contentTabsetWeightRef = useRef<number>(70);
    const { layout: copilotLayout, selectSettingView } = useCopilotLayout();
    const mainModel = useMemo(() => Model.fromJson(mainModelJson), []);
    const wallFormingModel = useMemo(() => Model.fromJson(wallFormingModelJson), []);

    const wallFormingFactory = useCallback((node: TabNode) => {
        const component = node.getComponent();

        if (component === 'content') {
            return (
                <div className="flex flex-col w-full h-full overflow-hidden">
                    <ContentContainer key="content" />
                </div>
            );
        }
    }, []);

    const mainFactory = useCallback(
        (node: TabNode) => {
            const component = node.getComponent();

            if (component === 'wallForming') {
                return (
                    <div className="sublayout wallforming relative w-full h-full bg-background-default m-0 p-0 overflow-hidden">
                        <Layout
                            key="wallForming-layout"
                            factory={wallFormingFactory}
                            model={wallFormingModel}
                            realtimeResize={false}
                        />
                    </div>
                );
            }
            if (component === 'settings') {
                return (
                    <div className="relative w-full h-full bg-background-default m-0 p-0 overflow-hidden">
                        <SettingContainer copilotLayout={copilotLayout} settingModelRef={settingModelRef} />
                    </div>
                );
            }
        },
        [wallFormingFactory, wallFormingModel, copilotLayout]
    );

    const resetNodeSize = useCallback((model: Model) => {
        model.visitNodes((node) => {
            if (node instanceof TabSetNode && node.isMaximized()) {
                model.doAction(Actions.maximizeToggle(node.getId()));
            }
        });
    }, []);

    const resetLayout = useCallback(() => {
        const screenWidth = window.screen.width;
        contentTabsetWeightRef.current = screenWidth >= 1920 ? 60 : 50;
        settingTabsetWeightRef.current = screenWidth >= 1920 ? 25 : 30;

        mainModel.doAction(Actions.updateNodeAttributes('content-tabset', { weight: contentTabsetWeightRef.current }));
        const settingTab = mainModel.getNodeById('setting-tab');
        const settingTabset = settingTab?.getParent();
        if (settingTabset instanceof TabSetNode) {
            mainModel.doAction(
                Actions.updateNodeAttributes(settingTabset.getId(), { weight: settingTabsetWeightRef.current })
            );
        }

        wallFormingModel.doAction(Actions.updateNodeAttributes('content-tab', { weight: 100 }));

        if (settingModelRef.current) {
            settingModelRef.current.doAction(Actions.updateNodeAttributes('options-tab', { weight: 65 }));
            settingModelRef.current.doAction(Actions.updateNodeAttributes('preview-tab', { weight: 35 }));
            resetNodeSize(settingModelRef.current);
        }

        resetNodeSize(mainModel);
        resetNodeSize(wallFormingModel);
    }, [mainModel, resetNodeSize, wallFormingModel]);

    const resizeTab = useCallback(
        (tabSetNode: TabSetNode | BorderNode) => {
            let node = mainModel.getNodeById(tabSetNode.getId());
            if (node && node instanceof TabSetNode) {
                mainModel.doAction(Actions.maximizeToggle(node.getId()));
                return;
            }

            node = wallFormingModel.getNodeById(tabSetNode.getId());
            if (node && node instanceof TabSetNode) {
                wallFormingModel.doAction(Actions.maximizeToggle(node.getId()));
            }
        },
        [mainModel, wallFormingModel]
    );

    const onRenderTabSet = useCallback(
        (tabSetNode: TabSetNode | BorderNode, renderValues: ITabSetRenderValues) => {
            const isTabMaximized = tabSetNode.isMaximized();
            renderValues.buttons.push(
                <Tooltip key="reset-layout-button" label={t('labelResetLayout')}>
                    <Icon
                        className="flexlayout_button justify-center rounded hover:bg-background-action-hover"
                        icon={'restart_alt'}
                        size="xl"
                        onClick={resetLayout}
                    />
                </Tooltip>,
                <Tooltip key="resize-tab-button" label={isTabMaximized ? t('labelMinimizeTab') : t('labelMaximizeTab')}>
                    <Icon
                        className="flexlayout_button justify-center rounded hover:bg-background-action-hover"
                        icon={isTabMaximized ? 'close_fullscreen' : 'open_in_full'}
                        size="xl"
                        onClick={() => resizeTab(tabSetNode)}
                    />
                </Tooltip>
            );
        },
        [resetLayout, resizeTab, t]
    );

    const onRenderTab = useCallback(
        (node: TabNode, renderValues: ITabRenderValues) => {
            if (node.getComponent() === 'wallForming') {
                renderValues.buttons.push(<InputContextStatus key="input-context-status" />);
            }

            if (node.getId() === 'setting-tab' && node.getName() === t('copilotTitle')) {
                renderValues.leading = (
                    <Icon
                        aria-hidden="true"
                        className="animate-[copilot-glow_3s_ease-in-out_infinite] ml-1 text-text-accent"
                        icon="auto_awesome"
                        size="lg"
                    />
                );
            }
        },
        [t]
    );

    useEffect(() => {
        const updateSettingTabName = (newSettingView: SettingView | null) => {
            if (newSettingView === null) {
                const settingTab = mainModel.getNodeById('setting-tab');
                const settingTabset = settingTab?.getParent();
                const contentTabset = mainModel.getNodeById('content-tabset');
                if (settingTab instanceof TabNode && settingTabset instanceof TabSetNode) {
                    settingTabJsonRef.current = settingTab.toJson();
                    settingTabsetWeightRef.current = settingTabset.getWeight();
                    if (contentTabset instanceof TabSetNode) {
                        contentTabsetWeightRef.current = contentTabset.getWeight();
                    }
                    mainModel.doAction(Actions.deleteTab('setting-tab'));
                }
                return;
            }

            if (!mainModel.getNodeById('setting-tab') && settingTabJsonRef.current) {
                mainModel.doAction(
                    Actions.addTab(settingTabJsonRef.current, 'content-tabset', DockLocation.RIGHT, -1, true)
                );
                const restoredTab = mainModel.getNodeById('setting-tab');
                const restoredTabset = restoredTab?.getParent();
                if (restoredTabset instanceof TabSetNode) {
                    mainModel.doAction(
                        Actions.updateNodeAttributes(restoredTabset.getId(), {
                            weight: settingTabsetWeightRef.current
                        })
                    );
                    mainModel.doAction(
                        Actions.updateNodeAttributes('content-tabset', { weight: contentTabsetWeightRef.current })
                    );
                }
            }

            mainModel.doAction(
                Actions.updateNodeAttributes('setting-tab', {
                    name:
                        newSettingView === SettingView.COPILOT
                            ? t('copilotTitle')
                            : newSettingView === SettingView.AUTOMATED_PLACEMENT
                              ? t('labelAutomatedPlacement')
                              : t('labelCatalogue')
                })
            );
        };

        updateSettingTabName(usePlacementStore.getState().selectedSettingView);
        const subscribedSettingView = usePlacementStore.subscribe(
            (state) => state.selectedSettingView,
            updateSettingTabName
        );

        return () => {
            subscribedSettingView();
        };
    }, [mainModel, t]);

    return (
        <div className="relative flex w-full h-full min-h-0 min-w-0 bg-background-default border border-background-default overflow-hidden">
            <div className="relative min-h-0 min-w-0 flex-1">
                <Layout
                    key="main-layout"
                    factory={mainFactory}
                    model={mainModel}
                    realtimeResize={false}
                    onRenderTab={onRenderTab}
                    onRenderTabSet={onRenderTabSet}
                />
            </div>
            <SettingNavigatorContainer
                isCopilotFloating={copilotLayout.isFloating}
                onSettingViewSelect={selectSettingView}
            />
        </div>
    );
};
